import { useGomakeAxios } from "@/hooks/use-gomake-axios";
import { useRecoilState, useRecoilValue } from "recoil";
import { printNodePrintersState, IPrintNodePrinter } from "@/store/print-node-printers-state";
import {
  getAllPrintersFromPrintNodeApi,
  getAllGoMakePrintPrintersApi,
} from "@/services/api-service/print-node/print-node-endpoints";
import { useEffect } from "react";
import {
  printHouseUsesGoMakePrintProvider,
  printHouseUsePrintNode,
} from "@/store/print-house-profile";
import {
  isGoMakePrintProvider,
  PrintProvider,
} from "@/store/print-provider";
import { resolvePrinterBins } from "@/widgets/shared-admin-customers/add-product/settings/modals/add-contact-modal/sku-components/sku-printer-fields";

/** Loads printers from PrintNode or PMS based on the Use GoMake Print Manager checkbox. */
const usePrintHousePrinters = () => {
  const { callApi } = useGomakeAxios();
  const [state, setState] = useRecoilState(printNodePrintersState);
  const hasPrintNodeFeature = useRecoilValue(printHouseUsePrintNode);
  const usesGoMakePrintProvider = useRecoilValue(printHouseUsesGoMakePrintProvider);

  const normalizePrinters = (printers: IPrintNodePrinter[]) =>
    printers.map((printer) => ({
      ...printer,
      label: printer.label || printer.printerName,
    }));

  const getPrinterById = (printerId: number | string) => {
    const id = String(printerId);
    return state.find((printer) => String(printer.printerId) === id);
  };

  const getPrinterBins = (printerId: number | string): string[] => {
    return resolvePrinterBins(state, printerId);
  };

  const getPrinterNameByPrinterId = (printerId: number | string): string => {
    const printer = getPrinterById(printerId);
    return printer ? printer.printerName : "";
  };

  const fetchPrintNodePrinters = async () => {
    const callBack = (res) => {
      if (res.success) {
        setState(normalizePrinters(res.data || []));
      }
    };
    await getAllPrintersFromPrintNodeApi(callApi, callBack);
  };

  const fetchGoMakePrintPrinters = async () => {
    const callBack = (res) => {
      if (res.success) {
        setState(normalizePrinters(res.data || []));
      }
    };
    await getAllGoMakePrintPrintersApi(callApi, callBack);
  };

  /** Fetch for a specific provider (use when toggling before Recoil re-renders). */
  const refreshPrinters = async (provider?: string | PrintProvider) => {
    const useGoMake =
      provider !== undefined
        ? isGoMakePrintProvider(provider)
        : usesGoMakePrintProvider;

    setState([]);
    if (!hasPrintNodeFeature) {
      return;
    }
    if (useGoMake) {
      await fetchGoMakePrintPrinters();
      return;
    }
    await fetchPrintNodePrinters();
  };

  useEffect(() => {
    if (!hasPrintNodeFeature) {
      setState([]);
      return;
    }
    if (usesGoMakePrintProvider) {
      fetchGoMakePrintPrinters().then();
      return;
    }
    fetchPrintNodePrinters().then();
  }, [usesGoMakePrintProvider, hasPrintNodeFeature]);

  return {
    printers: state,
    isPrintHouseUsePrintNode: hasPrintNodeFeature,
    usesGoMakePrintProvider,
    getPrinterBins,
    getPrinterNameByPrinterId,
    refreshPrinters,
  };
};

export { usePrintHousePrinters };
